import { Alert, Box, Button, FormControl, FormLabel, Grid, Input, Typography } from '@mui/joy';
import { Stack } from '@mui/system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';

import { getI18nRadioLabel } from '@/components/form-elements/I18nRadioLabel';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { ButtonLink } from '@/components/router/ButtonLink';
import { RouterLink } from '@/components/router/RouterLink';
import { I18N_DE } from '@/i18n/de';
import { CreateOrganizationAdminLevelDto, OrganizationAdminLevelPermission } from '@/modules/api';
import {
  backendApiOrganizationsFindOneOrganizationAdminLevelOptions,
  backendApiOrganizationsRemoveOrganizationAdminLevelMutation,
  backendApiOrganizationsUpdateOrganizationAdminLevelMutation,
} from '@/modules/api/@tanstack/react-query.gen';
import { useOrganizationQuery } from '@/queries/useOrganizationQuery';
import { EntityActive } from '@/types/EntityActive';

export const Route = createFileRoute(
  '/_authenticated/_management/administration/organisationen/$organizationId/verwaltungsebenen/$adminLevelId',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: OrganizationAdminLevelDetailsRoute,
});

type FormData = Pick<CreateOrganizationAdminLevelDto, 'permission'> & {
  active: EntityActive;
};

function OrganizationAdminLevelDetailsRoute() {
  const { adminLevelId, organizationId } = Route.useParams();
  const navigate = useNavigate();

  const { data: organizationAdminLevel } = useQuery(
    backendApiOrganizationsFindOneOrganizationAdminLevelOptions({ path: { organizationId, adminLevelId } }),
  );

  const { data: organization } = useOrganizationQuery({
    adminLevels: true,
    users: true,
  });

  const { mutate, isPending } = useMutation(backendApiOrganizationsUpdateOrganizationAdminLevelMutation());

  const { mutateAsync: removeMutate, isPending: removeIsPending } = useMutation({
    ...backendApiOrganizationsRemoveOrganizationAdminLevelMutation(),
    onSuccess: () =>
      navigate({
        to: '/administration/organisationen/$organizationId',
        params: { organizationId },
      }),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FormData>();

  return (
    organizationAdminLevel && (
      <Stack
        gap={2}
        component="form"
        onSubmit={handleSubmit((formData) => {
          if (isPending) return;

          mutate({ path: { organizationId, adminLevelId }, body: formData });
        })}
      >
        <Typography level="h3">Zugeordnete Verwaltungsebene</Typography>

        {organization?.active === false && (
          <Alert color="danger" sx={{ marginBottom: 2 }} variant="outlined">
            <Box>
              <Typography level="title-lg">Inaktive Organisation</Typography>
              <Typography>
                Die Organisation{' '}
                <RouterLink
                  to="/administration/organisationen/$organizationId"
                  params={{ organizationId: organization.id }}
                  sx={{
                    fontSize: 'inherit',
                    color: 'inherit',
                    textDecorationColor: 'inherit',
                  }}
                >
                  {organization?.name} ({I18N_DE.OrganizationType[organization.type]})
                </RouterLink>{' '}
                ist inaktiv. Die Aktivierung der Verwaltungsebene ist erforderlich um Benutzer*innen Zugriff auf diese
                zu gewähren.
              </Typography>
            </Box>
          </Alert>
        )}
        <Grid container spacing={8}>
          <Grid md={6}>
            <Stack gap={2}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input value={organizationAdminLevel?.adminLevel?.name} readOnly />
              </FormControl>

              <FormControl>
                <FormLabel>Ebene</FormLabel>
                <Input
                  value={
                    organizationAdminLevel.adminLevel && I18N_DE.AdminLevelType[organizationAdminLevel.adminLevel?.type]
                  }
                  readOnly
                />
              </FormControl>
            </Stack>
          </Grid>

          <Grid md={6}>
            <Stack gap={2}>
              <FormControl>
                <FormLabel>Verwaltungsebene</FormLabel>
                <Input value={organizationAdminLevel.organization?.name} readOnly />
              </FormControl>

              <FormControl>
                <FormLabel>Ebene</FormLabel>
                <Input
                  value={
                    organizationAdminLevel.organization &&
                    I18N_DE.OrganizationType[organizationAdminLevel.organization?.type]
                  }
                  readOnly
                />
              </FormControl>
            </Stack>
          </Grid>
        </Grid>
        <FormControl>
          <FormLabel>Berechtigung</FormLabel>
          <Controller
            control={control}
            name="permission"
            defaultValue={organizationAdminLevel.permission}
            render={({ field }) => (
              <RadioWithDescriptionGroup
                {...field}
                options={Object.values(OrganizationAdminLevelPermission)}
                optionLabel={getI18nRadioLabel({
                  i18nTitle: I18N_DE.OrganizationAdminLevelPermission,
                  i18nDescription: I18N_DE.OrganizationAdminLevelPermissionDescription,
                })}
              />
            )}
          />
        </FormControl>

        <Stack direction="row" gap={2} marginTop={4}>
          <ButtonLink
            variant="outlined"
            to="/administration/organisationen/$organizationId"
            params={{ organizationId }}
          >
            Organisation
          </ButtonLink>

          <Button
            color="danger"
            onClick={async () => {
              if (removeIsPending) return;

              await removeMutate({ path: { adminLevelId, organizationId } });
            }}
            sx={{ marginRight: 'auto' }}
            disabled={removeIsPending}
            loading={removeIsPending}
          >
            Zuordnung aufheben
          </Button>

          <Button type="submit" disabled={!isDirty || isPending} loading={isPending}>
            Speichern
          </Button>
        </Stack>
      </Stack>
    )
  );
}
